import type { FormDeclaration } from './models/FormDeclaration';

export interface AppSettings {
  apiBaseUrl: string;
  csrfToken?: string;
  captchaKey?: string;
  guid?: string;
  formData?: string | Record<string, unknown>;
  submitResponse?: SubmitResponse;
  saveInfo?: SaveInfo;
  links?: Record<string, string>;
  nzbnApiUrl?: string;
  nzbnApiToken?: string;
  googleApiKey?: string;
  notifiableEventConfirmationMessage?: string;
  submitButtonLabel?: string;
  stepIndicatorStyle?: 'simple' | 'enhanced';
  continueAlwaysEnabled?: boolean;
  hideStepTitlesWithQuestions?: boolean;
  disableRecaptcha?: boolean;
  requirePaymentTerms?: boolean;
  paymentFee?: Record<string, number>;
  fieldOptions?: Record<string, QuestionOption[] | Record<string, QuestionOption[]>>;
  /**
   * Set to mirror the active step into the URL, e.g. 'step-' gives '.../step-2/'.
   * Must match the route the form's controller serves those URLs from.
   */
  stepUrlPrefix?: string;
  concernDetailsThingsToInclude?: string;
  /**
   * Dev-only. When true, `?demoPrefill=1` fills the form with valid sample answers.
   */
  demoPrefillEnabled?: boolean;
}

export interface ValidationResponse {
  valid?: boolean;
  message?: string;
}

export interface UploadedFile {
  identifier?: string;
  originalFileName?: string;
  savedFileName?: string;
  fileSize?: number;
  formattedFileSize?: string;
  removed?: boolean;
}

export interface QuestionOption {
  helpText?: string;
  supportText?: string;
  value: string;
  label?: string;
  categories?: string[];
}

export interface Location {
  country?: string;
  region?: string;
  locality?: string;
  postCode?: string;
  formattedAddress?: string;
  lat?: number;
  lng?: number;
}

export interface SubmitResponse {
  action?: string;
  success?: boolean;
  message?: string;
  reference?: string;
  date?: string;
  info?: unknown;
}

export interface SaveInfo {
  saveDate?: string;
  expiryDate?: string;
  expiryDays?: number;
}

export interface DeclarationSchema {
  helpText?: string;
  supportText?: string;
  characterDeclarationLinkKey?: string;
}

export type FieldType =
  | 'Text'
  | 'TextArea'
  | 'Email'
  | 'Phone'
  | 'Numeric'
  | 'Date'
  | 'DateText'
  | 'MonthYear'
  | 'Radio'
  | 'RadioList'
  | 'CheckList'
  | 'CheckBox'
  | 'DropDown'
  | 'Location'
  | 'FileUpload'
  | 'ReadOnly'
  | 'Message'
  | 'Callout'
  | 'Fee'
  | 'Name'
  | 'NZBN'
  | 'SplitQuantity'
  | 'NotifiableEvents'
  | 'DropDownDependant'
  | 'RadioDependant';

export interface FieldSchema {
  type: FieldType;
  id: string;
  label?: string;
  optional?: boolean;
  helpText?: string;
  supportText?: string;
  openHelpText?: boolean;
  options?: QuestionOption[];
  maxLength?: number;
  rows?: number;
  maxDate?: string;
  maxFiles?: number;
  maxFileSize?: number;
  maxCombinedFileSize?: number;
  allowedFileTypes?: string[];
  listType?: string;
  text?: string;
  calloutStatus?: string;
  calloutId?: string;
  dependsOn?: string;
  dependantOptions?: Record<string, QuestionOption[]>;
  min?: number;
  max?: number;
  minTotal?: number;
  size?: string;
  value?: string;
}

export interface GroupSchema {
  id: string;
  label?: string;
  showTopDivider?: boolean;
  fields: FieldSchema[];
}

export interface GroupSetSchema {
  type: 'groupSet';
  groupSetId: string;
  label?: string;
  supportText?: string;
  helpText?: string;
  labelAdd?: string;
  labelRemove?: string;
  min?: number;
  max?: number;
  group: GroupSchema;
}

export type SectionGroupSchema = GroupSchema | GroupSetSchema;

export function isGroupSetSchema(group: SectionGroupSchema): group is GroupSetSchema {
  return 'type' in group && group.type === 'groupSet';
}

export interface SectionSchema {
  id: string;
  label?: string;
  supportText?: string;
  showTopDivider?: boolean;
  groups?: SectionGroupSchema[];
}

export interface StepSchema {
  id: string;
  label: string;
  title: string;
  navigationLabel?: string;
  component?: string;
  sections: SectionSchema[];
}

export interface FormSchema {
  steps: StepSchema[];
  declaration?: DeclarationSchema;
}

export interface IDeclaration {
  validate(): void;
  completed(): boolean;
  showValidationMsg(): boolean;
}

export interface FormData {
  guid: string;
  declaration: IDeclaration | null;
  acceptPaymentTermsAndConditions: boolean;
  steps: import('./models/Step').Step[];
}

export type { FormDeclaration };
